/** API-Typen, parallel zu hetzner/webhook/planer.py Pydantic-Models. */

export type SlotSummary = {
  id: string;
  termin_id: string | null;
  person_id: string;
  person_typ: "helfer" | "inhaber";
  person_name: string;
  start_at: string;
  end_at: string;
  pause_stunden: number;
  stundenlohn: number;
  /** Zusatzkosten am Slot — Auslagen, Anfahrt, Material-Pauschalen. */
  zusatzkosten: number;
  /**
   * Wie dieser Einsatz vergütet wird. Bei "pauschale" ist `stundenlohn`
   * bedeutungslos — bezahlt wird der Festpreis aus `pauschale_id`.
   */
  verguetungsart: "stundenlohn" | "pauschale";
  /** Festpreis-Verknüpfung (projekt_helfer_pauschale), nur bei "pauschale". */
  pauschale_id: string | null;
  /** Freitext-Notiz am Slot (z.B. „Anfahrt 50 km + Maut"). */
  note: string;
  /** Ausgeklammerte Zeitfenster (Migration 206) — ihre Stunden stecken schon in pause_stunden. */
  luecken?: { von: string; bis: string; termin_id: string; stunden: number }[];
  confirm_status: string;
  einladungs_status: string;
  stunden_netto: number;
};

export type Termin = {
  id: string;
  projekt_id: string;
  projekt_title: string;
  /** Der Ort, der gilt: ein abweichend gewählter, sonst der Standard. */
  projekt_ort: string;
  /** Ort ohne Abweichung — Baustelle, sonst Lieferadresse des Kunden. */
  standard_ort?: string;
  /** "" = Standard; sonst projekt | kunde:liefer | kunde:rechnung | lieferant:<id>. */
  einsatzort_quelle?: string;
  /** Beschriftung des abweichenden Orts, z. B. der Lieferant. */
  einsatzort_name?: string;
  /** Kunde des Projekts — Sprungziel /kunden?kunde=… aus dem Termin heraus. */
  kunde_id: string;
  kunde_name: string;
  title: string;
  notiz: string;
  start_at: string;
  end_at: string;
  farbe: string;
  status: string;
  planung_typ: string;
  benoetigte_helfer: number;
  /** Ausdrücklich für alle Helfer ausgeschriebene Stellen. */
  offene_stellen: number;
  /** Noch unbesetzte, ausdrücklich in der Team-App ausgeschriebene Stellen. */
  freie_stellen: number;
  zugewiesene_helfer: string[];
  eingeladene_helfer: string[];
  abgelehnte_helfer: string[];
  zugewiesene_inhaber: string[];
  slots: SlotSummary[];
};

/** Ein Ort, der für einen Termin in Frage kommt (`GET /kontor/termin-orte`). */
export type TerminOrtOption = {
  quelle: string;
  art: "projekt" | "kunde" | "lieferant";
  name: string;
  /** Leer bei Lieferanten ohne hinterlegte Adresse — dort nachtragbar. */
  adresse: string;
  /** Lieferant hängt schon am Projekt (Belege, Mails). */
  am_projekt: boolean;
  /** Dieselbe Adresse wie der Standard-Ort. */
  ist_standard: boolean;
  lieferant_id?: string;
};

export type TerminOrte = { standard: string; standard_name: string; orte: TerminOrtOption[] };

export type TeamMember = {
  id: string;
  name: string;
  role: "helfer" | "inhaber" | string;
  active: boolean;
  hourly_rate: number;
  phone: string;
  email: string;
};

export type TeamMemberCreateBody = {
  name: string;
  role?: string;
  hourly_rate?: number;
  phone?: string;
  email?: string;
  active?: boolean;
};

export type TeamMemberPatchBody = Partial<{
  name: string;
  role: string;
  hourly_rate: number;
  phone: string;
  email: string;
  active: boolean;
}>;

/** Kennzahlen-Detail (GET /projekt/{id}/kennzahlen) — gespiegelt aus Desktop. */
export type KennzahlenHelfer = {
  person_id: string;
  person_name: string;
  stunden_netto: number;
  stundenlohn_avg: number;
  zusatzkosten: number;
  personalkosten: number;
  /** personalkosten = Stunden + Σ Pauschalen (additiv). */
  pauschale_summe?: number;
  pauschalen?: PauschaleZeile[];
};
export type PauschaleZeile = { id: string; bezeichnung: string; betrag: number };
/** Ein Einsatz (Slot) in der personenzentrierten Stundenliste (Spec S1). */
export type PersonStundenZeile = {
  slot_id: string;
  termin_id: string;
  datum: string;        // YYYY-MM-DD
  start_at: string;
  end_at: string;
  von: string;          // HH:MM
  bis: string;
  pause_stunden: number;
  netto_stunden: number;
  stundenlohn: number;
  lohn: number;
  confirm_status: string;
  projekt_id: string | null;
  projekt_title: string;
};
export type PersonStunden = {
  person_id: string;
  person_name: string;
  von: string;
  bis: string;
  zeilen: PersonStundenZeile[];
  summe_stunden: number;
  summe_lohn: number;
};
/** Eine Pauschal-Position (Teilabschnitt) eines Helfers für ein Projekt. */
export type Pauschale = {
  id: string;
  helfer_id: string;
  helfer_name: string;
  bezeichnung: string;
  betrag: number;
  datum: string | null;   // geplantes Ausführungsdatum (YYYY-MM-DD)
  notiz: string;
  /** Gesetzt = als verdient freigegeben; erst dann zählt sie im Guthaben des Helfers. */
  freigegeben_am?: string;
  /** Termine an dieser Pauschale — davon noch nicht bestätigt/gelaufen. */
  slot_count?: number;
  slot_offen?: number;
  /** Mit welcher Helferrechnung abgerechnet (Migration 188); leer = noch keine. */
  beleg_id?: string;
  beleg_datum?: string;
};
export type KennzahlenInhaber = {
  person_id: string;
  person_name: string;
  stunden_netto: number;
};
export type KennzahlenAusgabe = {
  id: string;
  ausgabedatum: string;
  lieferant: string;
  beschreibung: string;
  netto: number;
  ausgabe_typ: string;
};
export type ProjektKennzahlen = {
  projekt_id: string;
  nettowert: number;
  materialkosten_legacy: number;
  arbeitstage_legacy: number;
  material_summe: number;
  verpflegung_summe: number;
  material_fuer_gewinn: number;
  verpflegung_fuer_gewinn: number;
  personalkosten_helfer: number;
  owner_stunden: number;
  helfer_stunden: number;
  stundenbasis: number;
  gewinn: number;
  stundenlohn: number;
  helfer_zeilen: KennzahlenHelfer[];
  inhaber_zeilen: KennzahlenInhaber[];
  material_zeilen: KennzahlenAusgabe[];
  verpflegung_zeilen: KennzahlenAusgabe[];
};

export type Owner = {
  id: string;
  name: string;
  active: boolean;
};

export type Projekt = {
  id: string;
  title: string;
  status: string;
  kunde_name: string;
  ort: string;
  /** "bau" | "pflege" | "" — Pflege wird in Liste/Suche ausgeblendet. */
  projektart: string;
};

/** Detail-Sicht — Stammdaten eines Projekts (GET /projekt/{id}). */
export type ProjektDetail = {
  id: string;
  title: string;
  status: string;
  kunde_id: string;
  kunde_name: string;
  ort: string;
  nettowert: number | null;
  materialkosten: number | null;
  personalkosten: number | null;
  notes: string;
  prioritaet: string;
  projektart: string;
  verantwortlicher: string;
  start: string;
  updated_at: string | null;
  /** Kunden-Kontakt — direkt am Projekt sichtbar. */
  kunde_phone: string;
  kunde_ansprechpartner: string;
  kunde_adresse: string;
  /** Pflegevertrags-Zuordnung: null = freie Pflege, gesetzt = vertragsgebunden. */
  pflegevertrag_id: string | null;
  pflegevertrag_nummer: string;
};

/** Gemeinsame Projektakte aus Kontor-Notizen und DEOS-Aufnahmen. */
export type ProjektAkteEintrag = {
  id: string;
  projekt_id: string;
  typ: string;
  titel: string;
  inhalt: string;
  quelle_typ: string;
  quelle_id: string;
  status: string;
  erstellt_von: string;
  ereignis_am: string;
  created_at: string;
  updated_at: string;
};

/** Wann das Bild entstand. Bestimmt zugleich den Nextcloud-Unterordner. */
export type AufnahmePhase = "bestandsaufnahme" | "verlauf" | "ergebnis" | "sonstiges";

export const AUFNAHME_PHASEN: { id: AufnahmePhase; label: string; hinweis: string }[] = [
  { id: "bestandsaufnahme", label: "Vorher", hinweis: "Zustand vor den Arbeiten — die Grundlage des Angebots" },
  // Die dritte Zeitebene: was während der Arbeit entsteht. Als einzige Phase nach
  // Tagen geordnet — sie wächst über Wochen, die anderen sind je ein Anlass.
  { id: "verlauf", label: "Verlauf", hinweis: "Bilder von den Arbeitstagen — aus der Team-App und vom Einsatz im Planer" },
  { id: "ergebnis", label: "Nachher", hinweis: "Ergebnis nach den Arbeiten — der Nachweis für den Kunden" },
  { id: "sonstiges", label: "Sonstiges", hinweis: "Schadensbild, Zufahrt, Materiallieferung" },
];

export type ProjektAufnahme = {
  id: string;
  notiz: string;
  dateiname: string;
  status: string;
  reihenfolge: number;
  erstellt_von: string;
  created_at: string;
  updated_at: string;
  quelle_typ: string;
  phase: AufnahmePhase;
  /** Tag, ZU DEM das Bild gehört (nur bei phase="verlauf" gesetzt). */
  arbeitstag?: string;
  /** Aus den Bilddaten (Migration 214): wann und wo das Bild entstand. */
  aufgenommen_am?: string;
  breite?: number | null;
  laenge?: number | null;
  vorschau_url: string;
  bild_url: string;
};

export type ProjektAkte = {
  projekt_id: string;
  projekt: Record<string, unknown>;
  briefing: Record<string, Array<{
    id: string;
    titel: string;
    inhalt: string;
    quelle_typ: string;
    ereignis_am: string;
  }>>;
  eintraege: ProjektAkteEintrag[];
  aufnahmen: ProjektAufnahme[];
  verlauf: ProjektVerlaufEintrag[];
  quellen: Record<string, Array<Record<string, unknown>>>;
  stand: string;
  fingerprint: string;
  kontext_hinweis: string;
};

export type ProjektVerlaufEintrag = {
  id: string;
  typ: "akte" | "aufnahme" | "termin" | "dokument" | "kommunikation" | "wissen";
  untertyp: string;
  titel: string;
  text: string;
  zeitpunkt: string;
  status?: string;
  quelle_typ: string;
  quelle_id: string;
  vertrauen: string;
  aufnahme_id?: string;
};

export type ProjektAkteCreateBody = {
  typ: string;
  titel: string;
  inhalt?: string;
  ereignis_am?: string;
};

/** Voller Kunden-Datensatz (GET /kunde/{id}) — Feld-Parität zur Desktop-CRM. */
export type KundeDetail = {
  id: string;
  name: string;
  kundentyp: string;
  typ: string;
  ansprechpartner: string;
  funktion: string;
  phone: string;
  email: string;
  website: string;
  ust_id: string;
  strasse: string;
  plz: string;
  ort: string;
  lieferadresse: string;
  rechnungsadresse: string;
  rechnung_strasse: string;
  rechnung_plz: string;
  rechnung_ort: string;
  kommunikationskanal: string;
  kontakte_json: string;
  notes: string;
  /** Briefanrede (Migration 164): "" | frau | herr | familie | paar | firma | du. Zeile baut lib/anrede.ts. */
  anrede: string;
  /** Abgelöst durch vorname/nachname (Migration 170) — nur noch Rückfall für den Altbestand. */
  anrede_name: string;
  /** Nur Privatkunden; `name` ist die daraus gebaute Anzeigezeile. */
  vorname: string;
  nachname: string;
  /** Monat "1".."12" -> Beschreibung; nur angehakte Monate. */
  pflege_monate: Record<string, string>;
  regelmaessige_pflege: boolean;
  updated_at: string | null;
};

export type Kunde = {
  id: string;
  name: string;
  kundentyp: string;
  typ: string;
  ansprechpartner: string;
  funktion: string;
  phone: string;
  email: string;
  website: string;
  ust_id: string;
  strasse: string;
  plz: string;
  ort: string;
  lieferadresse: string;
  rechnungsadresse: string;
  rechnung_strasse: string;
  rechnung_plz: string;
  rechnung_ort: string;
  kommunikationskanal: string;
  kontakte_json: string;
  notes: string;
  /** Briefanrede (Migration 164): "" | frau | herr | familie | paar | firma | du. Zeile baut lib/anrede.ts. */
  anrede: string;
  /** Abgelöst durch vorname/nachname (Migration 170) — nur noch Rückfall für den Altbestand. */
  anrede_name: string;
  /** Nur Privatkunden; `name` ist die daraus gebaute Anzeigezeile. */
  vorname: string;
  nachname: string;
  /** Monat "1".."12" -> Beschreibung; nur angehakte Monate. */
  pflege_monate: Record<string, string>;
  regelmaessige_pflege: boolean;
  /** Komplette einzeilige Lieferadresse — Projekt-Anlegen übernimmt diese. */
  adresse: string;
  updated_at: string | null;
};

/** Ein Pflegeeinsatz (= Pflegeprojekt) mit Gewinn je Einsatz. */
export type Pflegeeinsatz = {
  id: string;
  title: string;
  status: string;
  start: string;
  /** Kundenname — nur bei der globalen Liste gefüllt. */
  kunde_name: string;
  nettowert: number;
  gewinn: number;
  stundenlohn: number;
  owner_stunden: number;
};

/** Stammdaten-Felder eines Kunden — gemeinsam für Create-Body & Patch-Body. */
export type KundeStammfelder = {
  kundentyp: string;
  typ: string;
  ansprechpartner: string;
  funktion: string;
  phone: string;
  email: string;
  website: string;
  ust_id: string;
  strasse: string;
  plz: string;
  ort: string;
  lieferadresse: string;
  rechnungsadresse: string;
  rechnung_strasse: string;
  rechnung_plz: string;
  rechnung_ort: string;
  kommunikationskanal: string;
  kontakte_json: string;
  notes: string;
  anrede: string;
  vorname: string;
  nachname: string;
  /** JSON-Objekt { "1".. : Beschreibung } — Pflege-Monate serialisiert. */
  pflege_monate_json: string;
  regelmaessige_pflege: boolean;
};

/** Body für POST /kunde — voller Feldsatz. Pflicht: name. */
export type KundeCreateBody = { name: string } & Partial<KundeStammfelder>;

/** Body für PATCH /kunde/{id} — alle Felder optional. */
export type KundePatchBody = Partial<{ name: string } & KundeStammfelder>;

/** Body für POST /projekt/{id}/pauschalen — Pflicht: helfer_id + betrag. */
export type PauschaleCreateBody = { helfer_id: string; betrag: number } & Partial<{ bezeichnung: string; datum: string | null; notiz: string }>;

/** Body für POST /projekt — Pflichtfelder Title + Kunde. */
export type ProjektCreateBody = {
  title: string;
  kunde_id: string;
} & Partial<{
  ort: string;
  status: string;
  notes: string;
  start: string;
  prioritaet: string;
  verantwortlicher: string;
  projektart: string;
  nettowert: number;
  materialkosten: number;
  personalkosten: number;
}>;

/** Body für PATCH /projekt/{id} — nur gesetzte Felder werden geändert. */
export type ProjektPatchBody = Partial<{
  title: string;
  kunde_id: string;
  ort: string;
  status: string;
  notes: string;
  start: string;
  prioritaet: string;
  verantwortlicher: string;
  projektart: string;
  nettowert: number;
  /** true = nettowert gilt so, wie er hier steht, und wird vom Belegstand nicht überschrieben. */
  nettowert_manuell: boolean;
  materialkosten: number;
  personalkosten: number;
}>;


// ─── Tages-Events ──────────────────────────────────────────────────────────

/**
 * Ein Punkt der Terminvorbereitung — was an diesem Tag bereitstehen muss.
 *
 * „20 t Mineralgemisch bestellen", „Bagger bestellen": Text plus optional der
 * Lieferant aus dem Stamm. Der Bereich hatte einmal Kategorie, Uhrzeit und
 * Kosten; die Kosten flossen sogar in eine zweite, geschätzte Gewinnzahl. Das
 * war nie gemeint und ist entfallen — hier wird geplant, nicht gerechnet.
 *
 * `kategorie` und `uhrzeit` kommen weiterhin vom Server, weil die Helfer-App
 * sie noch setzt; Kontor zeigt und schreibt sie nicht.
 */
export type TagesEvent = {
  id: string;
  termin_id: string;        // FK auf arbeitszeit_planung
  datum: string;            // YYYY-MM-DD — aus termin.start_at, read-only
  kategorie: string;        // Altbestand, von Kontor nicht mehr gesetzt
  titel: string;
  details: string;
  projekt_id: string;       // aus termin
  projekt_title: string;    // Convenience aus JOIN
  uhrzeit: string;          // Altbestand, von Kontor nicht mehr gesetzt
  erledigt: boolean;
  lieferant_id: string;
  lieferant_name: string;
  updated_at: string | null;
};

/** Body für POST /event — termin_id und titel sind Pflicht. */
export type EventCreateBody = {
  termin_id: string;
  titel: string;
  details?: string;
  lieferant_id?: string;
  erledigt?: boolean;
};

/** Body für PATCH /event/{id} — termin_id nicht änderbar. */
export type EventPatchBody = Partial<{
  titel: string;
  details: string;
  lieferant_id: string;       // "" = Lieferant entfernen
  erledigt: boolean;
}>;

/**
 * Zeile der Projektübersicht (GET /projekte/uebersicht) — Superset von `Projekt`.
 * Trägt alles, was die Projektkarte der Desktop-App zeigt.
 */
export type ProjektUebersicht = {
  id: string;
  title: string;
  status: string;
  kunde_id: string;
  kunde_name: string;
  ort: string;
  projektart: string;
  prioritaet: string;
  verantwortlicher: string;
  start: string;
  abschluss: string;
  nettowert: number;
  /** Σ erfasster Ausgaben; ohne Ausgabenzeilen das Stammdatenfeld. */
  materialkosten: number;
  /** Live aus termin_slot plus Helfer-Pauschalen. */
  personalkosten: number;
  updated_at: string | null;
  hat_angebot: boolean;
  hat_rechnung: boolean;
};

// ─── Projekt-Ausgaben (Material · Verpflegung · Betrieb · Helfer) ──────────

export type AusgabeTyp = "lieferant" | "helfer" | "betrieb" | "verpflegung";

/** Eine Ausgabenzeile eines Projekts (GET /projekt/{id}/ausgaben). */
export type Ausgabe = {
  id: string;
  projekt_id: string;
  netto: number;
  beschreibung: string;
  lieferant: string;
  /**
   * WER bezahlt hat: "inhaber" = aus der Inhabertasche, "gbr" = die GbR.
   * Kanonisch laut Migration 020/056/097, identisch zu `beleg.zahlungsweg`.
   * WIE bezahlt wurde, steht getrennt in `zahlungsart` (konto|bar).
   */
  zahlungsweg: string;
  ausgabedatum: string;
  inhaber_id: string;
  inhaber_name: string;
  ausgabe_typ: AusgabeTyp | string;
  helfer_id: string;
  helfer_name: string;
  belegpfad: string;
  bezahlt_am: string;
  /** Beleg, aus dem diese Ausgabe gebucht wurde ("" = von Hand erfasst). */
  beleg_id: string;
};

/**
 * Beleg eines Projekts (GET /projekt/{id}/belege) — Teilmenge von ScanFlows
 * BelegOut, gerade so viel wie die Dokumenten-Sektion anzeigt.
 */
/** Eine Lieferschein-Position in der Projektsicht (Migration 193). */
export type LieferPositionKurz = {
  /** Katalogname, wenn sicher zugeordnet — sonst wie auf dem Lieferschein. */
  name: string;
  menge: number;
  einheit: string;
  /** Noch keinem Material sicher zugeordnet. */
  offen: boolean;
};

export type ProjektBeleg = {
  id: string;
  beleg_typ: string;
  lieferant: string;
  lieferant_firma_id: string;
  datum: string;
  netto: number;
  bruttobetrag: number;
  status: string;
  ausgabe_typ: string;
  is_bewirtung: boolean;
  /** Gesetzt = als Projektausgabe gebucht; leer = fehlt in der Gewinnermittlung. */
  projekt_ausgabe_id: string;
  /** Gesetzt = einer Bankzeile zugeordnet; wichtig vor dem Entfernen einer Buchung. */
  kontobewegung_id: string;
  projekt_id: string;
  projekt_title: string;
  belegpfad: string;
  notiz: string;
  /** Nur bei Lieferscheinen: was geliefert wurde (für dieses Projekt). */
  lieferpositionen: LieferPositionKurz[];
};

/**
 * Auftragswert und Zahlungsstand eines Projekts (GET /projekt/{id}/auftragswert).
 *
 * Abgeleitet aus den Belegen: nur eine Schlussrechnung als jüngster Beleg senkt
 * den Wert, alles andere kann ihn nur heben. Regel in kontor_projekt_wert.py.
 */
export type ProjektAuftragswert = {
  projekt_id: string;
  auftragswert: number;
  /** false = durch keinen Beleg gedeckt, der von Hand gepflegte Wert gilt. */
  belegt: boolean;
  herkunft: "schlussrechnung" | "rechnungen" | "angebot" | "handwert" | "kein_beleg" | string;
  herkunft_text: string;
  gespeichert: number;
  abweichung: boolean;
  gestellt: number;
  bezahlt: number;
  offen: number;
  nicht_abgerechnet: number;
  anzahl_angebote: number;
  anzahl_rechnungen: number;
  anzahl_abschlaege?: number;
  hat_schlussrechnung: boolean;
  /** Jüngste Rechnung trägt den Vermerk „schließt den Auftrag ab" (Migration 216). */
  abgeschlossen_durch_vermerk?: boolean;
  /** true = der Wert ist eine Schätzung ohne Beleg; erzeugt nie eine offene Position. */
  geschaetzt: boolean;
  /** true = von Hand gesetzt (Migration 146); der Belegstand überschreibt ihn dann nicht mehr. */
  manuell: boolean;
  /** ISO-Zeitpunkt des Umschaltens auf Hand; "" solange automatisch gerechnet wird. */
  manuell_am: string;
  /** Was aus den Belegen käme — bei „Auto" identisch mit auftragswert. */
  auto_wert: number;
  auto_herkunft_text: string;
  hinweis: string;
  status_aktuell: string;
  /** "" = kein Vorschlag; sonst ein Wert aus den Projektstatus-Optionen. */
  status_vorschlag: string;
  status_grund: string;
  /** Vor Ort bar bezahlt, ohne Rechnung (Migration 207). */
  bar_brutto?: number;
  bar_netto?: number;
  bar_am?: string;
  abgeschlossen_durch_barzahlung?: boolean;
  /** Angenommene Angebote, die zum Auftragswert zählen (Migration 222). */
  angebote_zaehlend?: { id: string; nummer: string; netto: number }[];
  /** Weitere angenommene Angebote, bei denen noch offen ist, wie sie zum Hauptangebot gehören. */
  angebote_frage?: AngebotFrage[];
  /** Schon geklärte Zusammenhänge — mit Rücknahme. */
  angebote_rollen?: { id: string; nummer: string; rolle: AngebotRolle; zu: string }[];
  /** Abgeschlossen: Auftragswert = Summe der Rechnungen (20.10.2026). */
  abgeschlossen_fest?: boolean;
  /** Abgeschlossen, aber keine Rechnung zugeordnet (Altbestand) — der alte Wert bleibt. */
  ohne_rechnung?: boolean;
};

export type AngebotRolle = "ersetzt" | "enthalten" | "zusaetzlich";
export type AngebotFrage = {
  id: string; nummer: string; netto: number;
  haupt_id: string; haupt_nummer: string; haupt_netto: number;
};

/** Vor Ort bar bezahlt (GET/PUT /projekt/{id}/barzahlung). */
export type Barzahlung = {
  brutto: number;
  netto: number;
  ust_satz: number;
  zahlungsdatum: string;
  empfaenger_owner_id: string;
  empfaenger_name: string;
  vermerk: string;
};

/** Rechnung desselben Kunden ohne Projektbezug (GET /projekt/{id}/rechnungen-kandidaten). */
export type RechnungKandidat = {
  id: string;
  nummer: string;
  kind: string;
  status: string;
  issue_date: string;
  netto: number;
  brutto: number;
  kunde_name: string;
};

export type AusgabeCreateBody = {
  netto: number;
  ausgabedatum: string;
} & Partial<{
  beschreibung: string;
  lieferant: string;
  zahlungsweg: string;
  inhaber_id: string;
  ausgabe_typ: string;
  helfer_id: string;
  belegpfad: string;
}>;

export type AusgabePatchBody = Partial<{
  netto: number;
  ausgabedatum: string;
  beschreibung: string;
  lieferant: string;
  zahlungsweg: string;
  inhaber_id: string;
  ausgabe_typ: string;
  helfer_id: string;
  belegpfad: string;
}>;

/** Eine Stundenzeile eines Projekts (GET /projekt/{id}/stunden). */
export type StundenZeile = {
  slot_id: string;
  termin_id: string;
  datum: string;
  person_id: string;
  person_name: string;
  person_typ: string;
  start_at: string | null;
  end_at: string | null;
  pause_stunden: number;
  stunden_netto: number;
  stundenlohn: number;
  zusatzkosten: number;
  kosten: number;
  verguetungsart: string;
  /** Verweis auf `projekt_helfer_pauschale`, falls der Einsatz pauschal bezahlt wird. */
  pauschale_id: string;
  confirm_status: string;
  note: string;
};

/** Body für POST /projekt/{id}/stunden — einen Einsatz nachtragen. */
export type StundeCreateBody = {
  person_id: string;
  person_typ: "helfer" | "inhaber";
  /** YYYY-MM-DD. Das Datum bestimmt, an welchen Termin der Einsatz gehängt wird. */
  datum: string;
  von: string;
  bis: string;
  pause_stunden: number;
  stundenlohn: number;
  zusatzkosten: number;
  note: string;
};

/** Body für PATCH /slots/{id} — nur gesetzte Felder werden geändert. */
export type SlotPatchBody = Partial<{
  start_at: string;
  end_at: string;
  pause_stunden: number;
  stundenlohn: number;
  zusatzkosten: number;
  note: string;
  confirm_status: string;
  einladungs_status: string;
}>;

/* ══ Belegstammdaten (Pflege aus Kontor heraus) ══════════════════════════ */

export type FirmenAdresse = { lines: string[]; postcode?: string | null; city?: string | null; country_code?: string };
export type FirmenBank = { name: string; iban: string; bic?: string | null };
export type Firmenstamm = {
  legal_name: string;
  address: FirmenAdresse;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  vat_id?: string | null;
  tax_number?: string | null;
  owners_line?: string | null;
  bank: FirmenBank;
  footer_line?: string | null;
  logo_path?: string | null;
};
export type FirmenstammAntwort = { exists: boolean; profile: Firmenstamm; logo_data_uri?: string };

export type TextBaustein = { label: string; text: string };
export type TextbausteinDefaults = {
  intro_invoice_label: string;
  intro_quote_label: string;
  notes_invoice_label: string;
  notes_quote_label: string;
};
export type Textbausteine = {
  intro_invoice: TextBaustein[];
  intro_quote: TextBaustein[];
  notes_invoice: TextBaustein[];
  notes_quote: TextBaustein[];
  defaults: TextbausteinDefaults;
};

export type KatalogPosition = {
  title: string;
  description: string;
  unit_label: string;
  unit_code: string;
  unit_price_net: number | string;
  vat_rate_percent: number | string;
};

/** Ein Nummernkreis samt Kontext, den man braucht, bevor man ihn anfasst. */
export type Nummernkreis = {
  kind: "invoice" | "quote";
  prefix: string;
  next_value: number;
  width: number;
  default_payment_term_days: number;
  number: string;
  /** Höchste bereits vergebene Nummer — Maßstab für den Zähler. */
  hoechste_vergeben: string;
};

/** Wiederkehrende Leistungsstruktur. Kunde, Nummer und Daten stehen NICHT drin. */
export type BelegVorlage = {
  id: string;
  name: string;
  beschreibung: string;
  kind: string;
  intro_paragraphs: string;
  notes_after_table: string;
  line_rows: Array<Record<string, unknown>>;
  positionen: number;
  summe_netto: number;
  created_by: string;
  updated_at: string;
};

/* ══ Termine eines Projekts ══════════════════════════════════════════════ */

export type ProjektTermin = {
  id: string;
  titel: string;
  start_at: string | null;
  end_at: string | null;
  datum: string;
  notiz: string;
  benoetigte_helfer: number;
  personen: string[];
  stunden: number;
};

/* ══ Lohnkonto: Belege und Auszahlungen ═════════════════════════════════ */

export type HelferBeleg = {
  id: string;
  lieferant: string;
  datum: string;
  bruttobetrag: number;
  netto: number;
  mwst: number;
  waehrung: string;
  beleg_typ: string;
  ausgabe_typ: string;
  status: string;
  projekt_id: string;
  projekt_title: string;
  notiz: string;
  created_at: string;
  is_bezahlt: boolean;
  belegpfad: string;
  /** Gesetzt = einer Bankzeile zugeordnet; leer = Ueberweisung noch offen. */
  kontobewegung_id: string;
  /** Die Auszahlung, die diese Rechnung beglichen hat — leer, solange sie offen ist. */
  auszahlung_id: string;
  auszahlung_am: string;
  auszahlung_betrag: number;
};

export type BelegBezahltBody = {
  betrag?: number;
  zahlungsdatum?: string;
  vermerk?: string;
  zahlungsweg?: "gbr_konto" | "inhaber";
  zahlungsart?: "ueberweisung" | "bar";
  inhaber_id?: string;
  nachweis?: boolean;
};

export type PayoutPatchBody = Partial<{
  betrag: number;
  zahlungsdatum: string;
  vermerk: string;
  zahlungsweg: "gbr_konto" | "inhaber";
  zahlungsart: "ueberweisung" | "bar";
  inhaber_id: string;
}>;

/** Ersatzbeleg für eine Ausgabe ohne Fremdbeleg (§ 14 UStG: kein Vorsteuerabzug). */
export type EigenbelegBody = {
  helfer_id?: string;
  member_lohn_auszahlung_id?: string;
  kontobewegung_id?: string;
  empfaenger?: string;
  betrag?: number;
  datum?: string;
  grund: string;
  verwendungszweck?: string;
  ausgabe_typ?: string;
};
